import { useCallback, useEffect, useState } from 'react';
import { Card, DataTable, Modal, FormField, TextInput, Toggle, StatusBadge, ConfirmDialog, EmptyState, PageHeader, Drawer } from '@/components/ui';
import type { DataTableColumn } from '@/components/ui/DataTable';
import { Icon } from '@/components/ui/Icon';
import { adminApi } from '@/shared/api/adminApi';
import { toast } from '@/components/ui/Toast';
import { ColorDots, LabelPill, TagActionBar, TAG_PRESETS } from './tagging/shared';
import { ActionBindingsEditor, cleanActionBindings, type ActionBinding } from './tagging/ActionBindingsEditor';

interface Disposition {
  id: number;
  code: string;
  label: string;
  color?: string | null;
  requires_followup: boolean;
  sort_order?: number;
  actions?: ActionBinding[] | null;
}
interface Usage { leads: number; calls: number }

const DEFAULT_COLOR = '#5a3df0';
const EMPTY = { code: '', label: '', color: DEFAULT_COLOR, requires_followup: false, sort_order: 0, actions: [] as ActionBinding[] };

export function Dispositions({ embedded = false }: { embedded?: boolean }) {
  const [rows, setRows] = useState<Disposition[]>([]);
  const [usage, setUsage] = useState<Record<string, Usage>>({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [drawer, setDrawer] = useState<{ mode: 'create' } | { mode: 'edit'; target: Disposition } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Disposition | null>(null);
  const [confirmInUse, setConfirmInUse] = useState<{ disp: Disposition; leads: number; calls: number } | null>(null);
  const [form, setForm] = useState({ ...EMPTY });

  const load = useCallback(() => {
    setLoading(true);
    void adminApi
      .dispositions()
      .then((res) => setRows(res.data as Disposition[]))
      .catch(() => toast('שגיאה בטעינת קודי סיום', 'danger'))
      .finally(() => setLoading(false));
    void adminApi.dispositionStats()
      .then((s) => setUsage(Object.fromEntries(s.map((x) => [x.code, { leads: x.leads, calls: x.calls }]))))
      .catch(() => setUsage({}));
  }, []);

  useEffect(() => { load(); }, [load]);

  const visible = search
    ? rows.filter((r) => r.label.includes(search) || r.code.toLowerCase().includes(search.toLowerCase()))
    : rows;

  function openCreate() {
    setForm({ ...EMPTY, sort_order: rows.length });
    setDrawer({ mode: 'create' });
  }
  function openEdit(d: Disposition) {
    setForm({ code: d.code, label: d.label, color: d.color || DEFAULT_COLOR, requires_followup: d.requires_followup, sort_order: d.sort_order ?? 0, actions: d.actions ?? [] });
    setDrawer({ mode: 'edit', target: d });
  }

  async function save() {
    if (!form.label) { toast('יש למלא תווית', 'danger'); return; }
    try {
      const actions = cleanActionBindings(form.actions);
      if (drawer?.mode === 'edit') {
        await adminApi.updateDisposition(drawer.target.id, {
          label: form.label, color: form.color || null,
          requires_followup: form.requires_followup, sort_order: form.sort_order, actions,
        });
        toast('עודכן', 'ok');
      } else {
        if (!form.code) { toast('יש למלא מזהה (קוד)', 'danger'); return; }
        await adminApi.createDisposition({ ...form, actions });
        toast('נוסף', 'ok');
      }
      setDrawer(null);
      setForm({ ...EMPTY });
      load();
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'שגיאה בשמירה';
      toast(msg, 'danger');
    }
  }

  function remove(d: Disposition) {
    const u = usage[d.code];
    if (u && (u.leads + u.calls) > 0) {
      setConfirmInUse({ disp: d, leads: u.leads, calls: u.calls });
      return;
    }
    void runRemove(d);
  }

  async function runRemove(d: Disposition) {
    try {
      await adminApi.deleteDisposition(d.id);
      toast('נמחק', 'ok');
      setDeleteTarget(null);
      setRows((prev) => prev.filter((r) => r.id !== d.id));
    } catch { toast('שגיאה במחיקה', 'danger'); }
  }

  // Reorder via up/down arrows — swaps positions and persists the whole order.
  async function move(idx: number, dir: -1 | 1) {
    const j = idx + dir;
    if (j < 0 || j >= rows.length) return;
    const next = [...rows];
    [next[idx], next[j]] = [next[j], next[idx]];
    setRows(next); // optimistic
    try {
      await adminApi.reorderDispositions(next.map((r) => r.id));
    } catch {
      toast('שגיאה בסידור', 'danger');
      load();
    }
  }

  const cols: DataTableColumn<Disposition>[] = [
    {
      key: '_order', he: '', width: 36,
      render: (r) => {
        const idx = rows.findIndex((x) => x.id === r.id);
        return (
          <div style={{ display: 'flex', flexDirection: 'column' }} onClick={(e) => e.stopPropagation()}>
            <button type="button" className="btn ghost" style={{ padding: '0 4px', height: 15, minHeight: 0, lineHeight: 1, transform: 'rotate(180deg)' }} disabled={idx === 0} onClick={() => move(idx, -1)} aria-label="העלה"><Icon name="chevronD" size={12} /></button>
            <button type="button" className="btn ghost" style={{ padding: '0 4px', height: 15, minHeight: 0, lineHeight: 1 }} disabled={idx === rows.length - 1} onClick={() => move(idx, 1)} aria-label="הורד"><Icon name="chevronD" size={12} /></button>
          </div>
        );
      },
    },
    { key: 'label', he: 'תווית', render: (r) => <LabelPill color={r.color || DEFAULT_COLOR} label={r.label} /> },
    { key: 'code', he: 'מזהה (קוד)', render: (r) => <span dir="ltr" style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>{r.code}</span> },
    { key: 'requires_followup', he: 'מעקב', render: (r) => (r.requires_followup ? <StatusBadge tone="warn" size="sm">מעקב אוטומטי</StatusBadge> : <span style={{ color: 'var(--muted)' }}>—</span>) },
    {
      key: '_usage', he: 'בשימוש',
      render: (r) => {
        const u = usage[r.code];
        if (!u || (u.leads + u.calls) === 0) return <span style={{ color: 'var(--muted)' }}>—</span>;
        return <span style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>{u.leads} לידים · {u.calls} שיחות</span>;
      },
    },
    {
      key: 'actions', he: 'פעולות', align: 'end',
      render: (r) => (
        <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }} onClick={(e) => e.stopPropagation()}>
          <button type="button" className="btn ghost" style={{ height: 26, padding: '0 6px' }} onClick={() => openEdit(r)} aria-label="עריכה"><Icon name="edit" size={15} /></button>
          <button type="button" className="btn ghost" style={{ height: 26, padding: '0 6px' }} onClick={() => setDeleteTarget(r)} aria-label="מחיקה"><Icon name="trash" size={15} color="var(--danger)" /></button>
        </div>
      ),
    },
  ];

  return (
    <div className="stack" style={embedded ? undefined : { padding: 'var(--gap)' }}>
      {!embedded && <PageHeader title="קודי סיום" />}

      <TagActionBar
        search={search}
        onSearch={setSearch}
        placeholder="חיפוש קוד או תווית…"
        createLabel="+ קוד חדש"
        onCreate={openCreate}
      />

      <Card padded={false}>
        <DataTable<Disposition>
          columns={cols} rows={visible} loading={loading} onRowClick={openEdit} dense
          emptyState={
            <EmptyState icon="info" title="אין עדיין קודי סיום" description="צור קוד סיום ראשון כדי לסווג תוצאות שיחה."
              action={<button type="button" className="btn primary" onClick={openCreate}>+ קוד חדש</button>} />
          }
        />
      </Card>

      <Drawer
        isOpen={!!drawer}
        onClose={() => setDrawer(null)}
        width={400}
        title={drawer?.mode === 'edit' ? `עריכת ${drawer.target.label}` : 'קוד סיום חדש'}
        footer={
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" className="btn primary" onClick={() => void save()}>שמור שינויים</button>
            <button type="button" className="btn" onClick={() => setDrawer(null)}>ביטול</button>
          </div>
        }
      >
        <div className="stack" style={{ gap: 18 }}>
          <FormField label="תווית (מה שהנציג רואה)" required>
            <TextInput value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="לדוגמה: עסקה" />
          </FormField>
          <FormField label="מזהה (אנגלית, ייחודי)" required
            hint={drawer?.mode === 'edit' ? 'הקוד משמש למערכת ולא ניתן לשינוי לאחר יצירתו.' : undefined}>
            <TextInput value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} dir="ltr" disabled={drawer?.mode === 'edit'} placeholder="deal" />
          </FormField>
          <FormField label="צבע התווית">
            <ColorDots value={form.color} onChange={(c) => setForm({ ...form, color: c })} />
          </FormField>
          <FormField label="סדר תצוגה" hint="ניתן לשנות את הסדר גם בחיצים בטבלה.">
            <TextInput type="number" value={String(form.sort_order)} onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) })} dir="ltr" />
          </FormField>
          <hr style={{ border: 'none', borderTop: '1px solid var(--line)', margin: 0 }} />
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
            <div>
              <div style={{ fontSize: 'var(--t-sm)', fontWeight: 600 }}>דורש מעקב אוטומטי</div>
              <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>יתזמן חזרה אוטומטית (24ש') אם הנציג לא קבע.</div>
            </div>
            <Toggle checked={form.requires_followup} onChange={(v) => setForm({ ...form, requires_followup: v })} />
          </div>

          <hr style={{ border: 'none', borderTop: '1px solid var(--line)', margin: 0 }} />
          <FormField label="הפעל בבחירת הקוד">
            <ActionBindingsEditor value={form.actions} onChange={(actions) => setForm({ ...form, actions })} />
          </FormField>
        </div>
      </Drawer>

      {deleteTarget && (
        <Modal isOpen onClose={() => setDeleteTarget(null)} title="מחיקת קוד סיום"
          footer={<><button type="button" className="btn" onClick={() => setDeleteTarget(null)}>ביטול</button><button type="button" className="btn danger" onClick={() => remove(deleteTarget)}>מחק</button></>}>
          <p>למחוק את קוד הסיום <strong>{deleteTarget.label}</strong> (<code dir="ltr">{deleteTarget.code}</code>)?</p>
          {usage[deleteTarget.code] && (usage[deleteTarget.code].leads + usage[deleteTarget.code].calls) > 0 && (
            <p style={{ color: 'var(--warn)', fontSize: 'var(--t-sm)' }}>קוד זה בשימוש ב-{usage[deleteTarget.code].leads} לידים ו-{usage[deleteTarget.code].calls} שיחות.</p>
          )}
        </Modal>
      )}

      <ConfirmDialog
        isOpen={!!confirmInUse}
        onClose={() => setConfirmInUse(null)}
        onConfirm={() => { if (confirmInUse) void runRemove(confirmInUse.disp); }}
        title="מחיקת קוד סיום"
        message={confirmInUse ? `קוד זה משויך ל-${confirmInUse.leads} לידים ו-${confirmInUse.calls} שיחות. למחוק בכל זאת?` : ''}
        confirmLabel="מחק"
        tone="danger"
      />
    </div>
  );
}

export { TAG_PRESETS as DISPOSITION_PRESETS };
